import React from 'react';
import './EntryOverlay.css';
import './ProjectCards.css';

interface RandomEntryProps {
  onReturnToMap: () => void;
  entryPosition?: { x: number; y: number };
  onNext?: () => void;
  nextLabel?: string;
}

interface Project {
  name: string;
  status: 'done' | 'in-progress' | 'paused';
  description: string;
  learned: string;
  repo?: string;
  demo?: string;
}

const PROJECTS: Project[] = [
  {
    name: 'wesknight.dev',
    status: 'in-progress',
    description:
      'This portfolio site — a 2D top-down game built with Three.js and React where you walk around a pixel-art map and explore houses that open into full content pages.',
    learned:
      'Sprite animation, tile-based collision, game loops, camera systems, and how to make a portfolio that actually reflects who I am.',
    repo: 'https://github.com/wes-fizzylabs/portfolio',
  },
  {
    name: 'AIdvisor',
    status: 'in-progress',
    description:
      'An AI-powered advisory platform exploring how LLMs can assist with real decision-making workflows — not just chat, but structured guidance.',
    learned:
      'Prompt engineering patterns, RAG architectures, and the gap between "AI demo" and "AI product."',
  },
  {
    name: 'Technical Interview Simulator',
    status: 'done',
    description:
      'A VR application built in Unity for Clemson University that simulates technical interview environments to help students practice under realistic pressure.',
    learned:
      'Unity/C# development, VR interaction design, spatial audio, and how immersive environments can reduce real-world anxiety.',
    repo: 'https://github.com/clemsonwesley/Technical-Interview-Simulator-',
  },
  {
    name: 'Project X',
    status: 'in-progress',
    description:
      'An exploratory AI project — experimenting with how AI changes the way we write, review, and ship software.',
    learned:
      'Agent patterns, tool-use architectures, and where AI genuinely helps vs. where it gets in the way.',
    repo: 'https://github.com/wes-fizzylabs/project_x',
  },
];

const STATUS_CONFIG = {
  'done': { label: 'Complete', className: 'status-done' },
  'in-progress': { label: 'In Progress', className: 'status-in-progress' },
  'paused': { label: 'Paused', className: 'status-paused' },
};

export const RandomEntry: React.FC<RandomEntryProps> = ({
  onReturnToMap,
  onNext,
  nextLabel,
}: RandomEntryProps) => {
  return (
    <div className="entry-overlay">
      <div className="entry-card accent-random">
        <div className="entry-header">
          <h2 className="entry-title">Projects</h2>
        </div>

        <div className="entry-body">
          <div className="entry-description">
            <p>
              Things I'm building, have built, or am thinking about building.
              Some are polished, some are experiments, and some are just
              getting started. That's kind of the point.
            </p>
          </div>

          <div className="project-grid">
            {PROJECTS.map((project) => {
              const status = STATUS_CONFIG[project.status];
              return (
                <div key={project.name} className="project-card">
                  <div className="project-card-header">
                    <h3 className="project-card-name">{project.name}</h3>
                    <span className={`project-status ${status.className}`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="project-card-description">
                    {project.description}
                  </p>
                  <div className="project-card-learned">
                    <span className="project-learned-label">What I learned</span>
                    <p>{project.learned}</p>
                  </div>
                  {(project.repo || project.demo) && (
                    <div className="project-card-links">
                      {project.repo && (
                        <a
                          href={project.repo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                        >
                          Repo
                        </a>
                      )}
                      {project.demo && (
                        <a
                          href={project.demo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                        >
                          Demo
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="entry-footer">
          <button className="entry-button" onClick={onReturnToMap}>
            &larr; Return to Map
          </button>
          {onNext && (
            <button className="entry-button" onClick={onNext}>
              Next: {nextLabel} &rarr;
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
